/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, useState } from "react";
import { useStores } from "@/app/csr-src/models";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FEACH_PARAMS } from "./constant";

import to from "await-to-js";
import { redirectPayResult, submitOrder } from "@/app/csr-src/apis/checkout/checkout.api";
import { PAY_TYPE, USER_ACTION_ID } from "@/app/csr-src/apis/checkout/constants/constants";

import { Button, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import OrderErrorModal from "./orderErrorModal";

import Card from "./payButton/card";
import PayPalButton from "./payButton/paypal";
import Default from "./payButton/default";
import KlarnaPay from "./payButton/klarna";
import GooglePayButton from "./payButton/google";
import WechatPayButton from "./payButton/wechat";
import ApplePayButton from "./payButton/apple";
import { fetchuseOrCancelCouponCode } from "@/app/csr-src/apis/coupon/coupon.api";

import Styles from "./payButtons.module.scss";
import { sendClickTrack } from "@/tracks/25281";
import PayRiskModal from "./payRiskModal";

interface PayButtonsProps {
  // onFinish: (res: any) => void; // 支付完成后的回调
  onFresh?: (params: any) => void;
  toBack: () => void;
  addCart: (sku: any[], userActionId: string) => void;
  type: string;
  immediatelyBuy?: boolean;
}
// enum枚举
const createOrderResultCode = {
  SUCCESS: "C-DM@60-CD@1.100000",
  VOUCHER_ERROR: "CSBB-DM@101710-CD@2.300011",
  PAY_ERROR: "C-DM@60112-CD@2.A00501",
  NO_ITEM: "CSBB-DM@1012-CD@2.200001",
  OUT_STOCK: "CSBB-DM@1013-CD@2.500102",
  ALL_OUT_STOCK_1: "VCYBB2C-DM@1013-CD@2.500101",
  ALL_OUT_STOCK_2: "CSBB-DM@1013-CD@2.500101",
  ACTIIVITY_LIMIT: "CSBB-DM@1035-CD@2.402005",
  /**
   * 调用支付风控失败
  */
  PAY_RISK_MANAGEMENT: "C-DM@60112-CD@2.A00502",
  /**
   * 
   * 提单风控拦截错误码
   * 与通用提单报错逻辑一致，错误码定义，为后续提供参考
   * 
   */
  ORDER_RISK_MANAGEMENT: "C- DM@102210-CD@2.200001",
};

const CANCEL_URL = "/profile/my-orders";

const PayButtons: FC<PayButtonsProps> = observer((props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { checkoutStore, orderStore } = useStores();
  const { onFresh } = props;
  const [orderErrorOpen, setOrderErrorOpen] = useState(false);
  const [commonErrorOpen, setCommonErrorOpen] = useState(false);
  const [voucherErrorOpen, setVoucherErrorOpen] = useState(false);
  const [riskErrorOpen, setRiskErrorOpen] = useState(false);
  const [commonError, setCommonError] = useState("");
  // 界面暂存的已选中券/码
  const [voucherItem, setVoucherItem] = useState < { activityId: string; batchId: string } > ({
    activityId: "",
    batchId: "",
  });

  const handleClose = () => {
    setCommonErrorOpen(false);
    checkoutStore.setCommonError({
      open: false,
      error: "",
    });
  };

  const onSubmitError = (res: any, type: string) => {
    console.log(res, type);

    setOrderErrorOpen(true);

    handleOrderErrorOpen(res, type);
  };
  const onlyClose = () => {
    setOrderErrorOpen(false);
  };

  // 券过期continue
  const voucherClose = async () => {
    const orderStr: any = {
      selected: false,
      batchId: voucherItem?.batchId,
      activityId: voucherItem?.activityId,
    };

    const requestData = {
      orderStr,
      ...FEACH_PARAMS,
      immediatelyBuy: props.immediatelyBuy,
    };
    const [err, res] = await to(fetchuseOrCancelCouponCode(requestData));
    if (res?.code !== "200") {
      UKToast.show({
        content: res?.message,
        icon: "error",
      });
    } else {
      UKToast.show({
        content: t("common.pageStatus.success"),
        icon: "success",
      });
      handleClose();
      props?.onFresh!({
        type: "code",
        data: null,
        refreshOrderSource: "saveCoupon",
      });
    }
  };

  const onCommonError = (res: string, code: string, data: any) => {
    if (code === createOrderResultCode.VOUCHER_ERROR) {
      setVoucherItem({
        activityId: data?.activityId,
        batchId: data?.batchId,
      });
      setVoucherErrorOpen(true);
    }
    setCommonError(res);
    setCommonErrorOpen(true);
  };

  // 处理全部无库存，部分无库存以及活动卡单的订单
  const handleOrderErrorOpen = (params: any, userActionId?: string) => {
    // 全部无库存
    const orderErrorMsg = {
      title: params.noStockTitle,
      btnText: t("checkout.common.continue"),
      onClose: () => {
        setOrderErrorOpen(false);
        props.toBack();
      },
      outStockList: params.outStockList,
    };

    // 部分无库存以及活动卡单的订单
    if (userActionId) {
      orderErrorMsg.onClose = () => {
        setOrderErrorOpen(false);
        const skuList = params.outStockList.map((sku: any) => {
          return {
            ...sku,
            num: sku.promoRemainNum || sku.num,
          };
        });
        props.addCart(skuList, userActionId);
      };
    }
    // 异常订单放入store中
    checkoutStore.setOrderErrorMsg(orderErrorMsg);
  };

  /**
   *
   * 提单逻辑处理
   * 失败统一处理；成功使用各支付方式的回调方法
   *
   */

  const handleSubmitOrder = async () => {

    sendClickTrack.SettleAccounts_OrderNew_Submit({ payWay: String(checkoutStore.payMethod), ...(checkoutStore as any).trackOrderInfo });

    const submitParams = getSubmitOrderParams();
    const [err, res] = await to(submitOrder(submitParams));
    console.log(res, 'submitOrder');
    checkoutStore.setPayResult({
      id: (res as any).data?.payId || "",
      orderId: res.data?.orderId || "",
    });

    if (!err) {
      switch (res.code) {
        // Success, 返回true，在各支付的中处理各种提单成功后的逻辑
        case createOrderResultCode.SUCCESS:
          return {
            success: true,
            data: res.data,
          };
        // Pay Error
        case createOrderResultCode.PAY_ERROR:
          UKToast.show({
            content: res.msg, // 弹窗内容
            icon: "error", // 可选：success, fail, loading, warn, info，影响图标
          });

          // 跳转到订单列表
          setTimeout(toOrderList, 3000);
          break;
        // Cart
        case createOrderResultCode.NO_ITEM:
          UKToast.show({
            content: t("common.message.noItems"),
            icon: "error",
            duration: 3000,
          });
          setTimeout(props.toBack, 3000);
          break;
        // Stock
        case createOrderResultCode.OUT_STOCK:
        case createOrderResultCode.ALL_OUT_STOCK_1:
        case createOrderResultCode.ALL_OUT_STOCK_2:
          // eslint-disable-next-line no-case-declarations
          const type = res.code === createOrderResultCode.OUT_STOCK ? USER_ACTION_ID.UN_SELECT : "";
          onSubmitError(res.data, type);
          break;
        case createOrderResultCode.ACTIIVITY_LIMIT:
          onSubmitError(res.data, USER_ACTION_ID.CHANGE_NUM);
          break;
        case createOrderResultCode.PAY_RISK_MANAGEMENT:
          setCommonError(res?.msg || t("common.message.networkError"));
          setRiskErrorOpen(true);
          break;
        default:
          onCommonError(res?.msg || t("common.message.networkError"), res.code, res.data);
          break;
      }
      return {
        success: false,
        data: null,
      };
    }

    UKToast.show({
      content: t("common.message.networkError"), // 弹窗内容
      icon: "error", // 可选：success, fail, loading, warn, info，影响图标
    });
    return false;
  };

  const getSubmitOrderParams = () => {
    return {
      orderStr: {
        callbackUrl: "/Checkout/Finish",
        payMethod: checkoutStore.payMethod,
        InvoiceInfo: {
          addressId: (checkoutStore.billingAddress as any)?.addressId,
        },
        extParam: JSON.stringify({
          // Credit card cancel url
          cancelUrl: CANCEL_URL,
          payEnum: (checkoutStore.currentPayInfo as any).payEnum,
          integrationType: (checkoutStore.currentPayInfo as any).integrationType,
          browserInfo: checkoutStore.isBnpCreditCardForm
            ? JSON.stringify(checkoutStore.browserInfo)
            : undefined,
        }),
        rememberPayAccount: (checkoutStore as any)?.currentPayInfo.rememberInfo?.rememberCheckbox || false, // 是否记住支付账号
        payAccountSeqNo: (checkoutStore as any).currentPayAccount?.payAccountSeqNo || "", // 支付账号id
      },
      immediatelyBuy: (checkoutStore.queryParams as any).immediatelyBuy,
      verticalTag: "cn_ybxt_b2c",
      lang: "en_GB",
      isPresale: false,
      ptFlag: false,
      client: "PC",
      clientVersion: "1.0.0",
    };
  };

  // 去订单列表
  const toOrderList = () => {
    orderStore.setOrderType("1");
    navigate("/profile/my-orders", { replace: true });
  };
  // 支付方式被风控，去订单详情
  const toOrderDetail = (isShowPayModal: boolean) => {
    // 关闭支付风控弹窗
    setRiskErrorOpen(false);
    orderStore.setIsShowPayModal(isShowPayModal);
    navigate("/profile/my-orders/order-details?orderId=" + (checkoutStore.payResult as any).orderId, { replace: true });
  };

  // 支付完成之后，重定向支付结果
  const redirectResult = async (requestData: any, closeLoading: () => void) => {
    await redirectPayResult({ ...requestData });
    return new Promise((resolve) => {
      closeLoading();
      handlePayFinish(checkoutStore.payResult);
      return resolve(true);
    });
  };

  const handlePayFinish = (res: any) => {
    navigate(`/Checkout/Finish/${res.id}/${checkoutStore.payMethod}/PC`, {
      replace: true,
    });
  };

  return (
    <div className={Styles.payButtonsBox}>
      {!checkoutStore.payMethod && <Default />}
      {/* 信用卡 */}
      {checkoutStore.payMethod === PAY_TYPE.CREDIT_CARD && (
        <Card onSubmit={handleSubmitOrder} toOrderList={toOrderList} />
      )}
      {/* paypal */}
      <div
        style={{
          display: checkoutStore.payMethod === PAY_TYPE.PAYPAL ? "block" : "none",
          width: "100%",
          height: "100%",
        }}
      >
        <PayPalButton
          onSubmit={handleSubmitOrder}
          toOrderList={toOrderList}
          onLoadingResult={redirectResult}
        />
      </div>

      {/* google pay */}
      <div
        style={{
          display: checkoutStore.payMethod === PAY_TYPE.GOOGLE_PAY ? "block" : "none",
        }}
      >
        <GooglePayButton
          onSubmit={handleSubmitOrder}
          toOrderList={toOrderList}
          onLoadingResult={redirectResult}
        />
      </div>
      {/* 分期 */}
      {checkoutStore.payMethod === PAY_TYPE.KLARNA && (
        <KlarnaPay
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          toOrderList={toOrderList}
        />
      )}

      {/* wechat pay */}
      {checkoutStore.payMethod === PAY_TYPE.WECHAT_PAY && (
        <WechatPayButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          toOrderList={toOrderList}
        />
      )}

      {/* Apple pay */}
      {checkoutStore.payMethod === PAY_TYPE.APPLE && (
        <ApplePayButton
          onSubmit={handleSubmitOrder}
          onLoadingResult={redirectResult}
          toOrderList={toOrderList}
        />
      )}

      {<OrderErrorModal open={orderErrorOpen} onlyClose={onlyClose} />}
      <UKDialog open={commonErrorOpen} onClose={handleClose} showCloseBtn={true}>
        <DialogTitle />

        {/* Error info */}
        <DialogContent className={Styles.payError}>{commonError}</DialogContent>

        {/* Button */}
        <DialogActions>
          {voucherErrorOpen === false ? (
            <Button onClick={props.toBack} className={Styles.button} size="small">
              {t("checkout.common.continue")}
            </Button>
          ) : (
            <Button onClick={voucherClose} className={Styles.button} size="small">
              {t("checkout.common.continue")}
            </Button>
          )}
        </DialogActions>
      </UKDialog>

      {/* 支付风控 */}

      {riskErrorOpen && <PayRiskModal
        open={riskErrorOpen}
        onClose={() => {
          toOrderDetail(false);
        }}
        onOk={() => {
          toOrderDetail(true);
        }}
        content={commonError} />}
    </div>
  );
});

export default PayButtons;
